// Token management
const TOKEN_KEY = 'ocpa_token';
const CUSTID_KEY = 'ocpa_custid';

const storeToken = (token: string, custid: string) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(CUSTID_KEY, custid);
};

export const getToken = (): string | null => {
  return sessionStorage.getItem(TOKEN_KEY);
};

export const getStoredCustId = (): string | null => {
  return sessionStorage.getItem(CUSTID_KEY);
};

export const clearToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(CUSTID_KEY);
};

export const setAuthToken = (token: string | null, custid: string | null) => {
  if (!token) {
    clearToken();
    return;
  }
  storeToken(token, custid || '');
};

export const isAuthenticated = (): boolean => {
  return !!getToken();
};

// ============== SAVED GRAPHS API ==============

const DASHBOARD_API_URL = import.meta.env.VITE_DASHBOARD_API_URL || 'https://ypqx15vs04.execute-api.us-east-1.amazonaws.com';

export interface SavedGraph {
  graphId: string;
  businessId: string;
  title: string;
  query: string;
  sql: string;
  visualizationHint: string;
  analyticsType?: string;
  description?: string;
  dateRange?: { start?: string; end?: string };
  createdAt: string;
  updatedAt?: string;
}

export interface SaveGraphRequest {
  title: string;
  query: string;
  sql: string;
  visualizationHint: string;
  analyticsType?: string;
  description?: string;
  dateRange?: { start?: string; end?: string };
}

export interface ExecutedGraph {
  status: string;
  error?: string;
  data: any[];
  sql: string;
  visualization_hint: any;
  meta?: any;
  analytics?: any;
  insight?: string;
  warnings?: string[];
  graphConfig: {
    graphId: string;
    title: string;
    query: string;
    visualizationHint: string;
    createdAt: string;
  };
}

// Save a graph to the dashboard
export const saveGraph = async (businessId: string, graphConfig: SaveGraphRequest): Promise<{ graphId: string; status: string }> => {
  const response = await fetch(`${DASHBOARD_API_URL}/api/graphs/${businessId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(graphConfig),
  });
  if (!response.ok) {
    throw new Error('Failed to save graph');
  }
  return response.json();
};

// Get all saved graphs for a business
export const getSavedGraphs = async (businessId: string): Promise<{ graphs: SavedGraph[] }> => {
  const response = await fetch(`${DASHBOARD_API_URL}/api/graphs/${businessId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch saved graphs');
  }
  return response.json();
};

// Get a specific saved graph config
export const getSavedGraph = async (businessId: string, graphId: string): Promise<SavedGraph> => {
  const response = await fetch(`${DASHBOARD_API_URL}/api/graphs/${businessId}/${graphId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch graph');
  }
  return response.json();
};

// Execute a saved graph (get fresh data)
export const executeSavedGraph = async (businessId: string, graphId: string): Promise<ExecutedGraph> => {
  const response = await fetch(`${DASHBOARD_API_URL}/api/graphs/${businessId}/${graphId}?execute=true`);
  if (!response.ok) {
    throw new Error('Failed to execute graph');
  }
  return response.json();
};

// Delete a saved graph
export const deleteSavedGraph = async (businessId: string, graphId: string): Promise<{ status: string; graphId: string }> => {
  const response = await fetch(`${DASHBOARD_API_URL}/api/graphs/${businessId}/${graphId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete graph');
  }
  return response.json();
};

// ============== END SAVED GRAPHS API ==============
